<template>
	<PageContainer title="Faturas">
		<Box class="faturas-container" space="medium">
			<form @submit.prevent="handleSubmit">
				<div class="radio">
					<span>Selecione o critério de consulta</span>

					<RadioButtons :options="consultOptions" name="consult" :modelValue="consult"
						@update:modelValue="$event => this.consult = $event" />
				</div>

				<div v-if="consult === 'period'" class="inputs">
					<div class="inputs-row">
						<Period class="input-date" legend="Data" :firstModelValue="initialDate" :secondModelValue="finalDate"
							@update:firstModelValue="$event => (this.initialDate = $event)"
							@update:secondModelValue="$event => (this.finalDate = $event)" :disabled="loading" />

						<Select :options="processoOptions" legend="Processo" placeholder="Selecione o processo"
							:modelValue="processo" @update:modelValue="$event => (this.processo = $event)" :disabled="loading" />

						<Select :options="statusOptions" legend="Status" placeholder="Selecione o status" :modelValue="status"
							@update:modelValue="$event => (this.status = $event)" :disabled="loading" />
					</div>
				</div>

				<div v-else-if="consult === 'student'" class="inputs">
					<div class="inputs-row">
						<Select :filtered="true" :options="alunos" legend="Aluno" placeholder="Digite o nome do aluno"
							:modelValue="aluno" @update:modelValue="$event => (this.aluno = $event)"
							:disabled="loadingAluno || loading" />

						<Input mask="###.###.###-##" legend="CPF" placeholder="000.000.000-00" :modelValue="cpf"
							@update:modelValue="$event => cpf = $event" class="input-full input-cpf" />
					</div>
				</div>

				<div v-else class="inputs">
					<div class="inputs-row">
						<Input class="input" legend="Código de barras" placeholder="Código de barras" :modelValue="barcode"
							@update:modelValue="$event => (this.barcode = $event)" :disabled="loading" />
					</div>
				</div>

				<div class="buttons">
					<Button type="button" @handle:click="clearInputs" text="Limpar" :fill="false" color="danger"
						:disabled="loading">
						<CloseCircleOutline :size="18" />
					</Button>

					<Button type="submit" text="Consultar" color="primary" :disabled="loading">
						<Magnify :size="18" />
					</Button>
				</div>
			</form>

			<div v-if="loading" class="d-flex justify-content-center">
				<component :animation-duration="1000" :is="'SpringSpinner'" :color="'#1C2D5A'" :size="80" class="mx-2">
				</component>
			</div>

			<div v-else-if="faturas.length > 0 && !loading" class="table-container">
				<div class="infos">
					<Input legend="Pago" :readonly="true" :modelValue="`R$ ${pago}`" />
					<Input legend="Pendente" :readonly="true" :modelValue="`R$ ${pendente}`" />
					<Input legend="Gratuidade" :readonly="true" :modelValue="`R$ ${gratuidade}`" />
					<Button type="button" @handle:click="openConfirmModal" text="Gerar boleto" color="primary"
						:disabled="loading || selectedFaturas.length < 1">
						<Barcode :size="18" />
					</Button>
				</div>

				<div class="table-header">
					<StatusLegend :statusOptions="statusLegendOptions" />
				</div>

				<Table :headers="['Status', 'Cód. Processo', 'Processo', 'Aluno', 'Data processo', 'Valor', 'Ações']"
					:selectable="true" :selected-items="selectedFaturas" :all-items="faturas" @select-all="selectAll"
					@toggle-selection="toggleSelection">
					<tr v-for="fatura in faturas" :key="fatura.id" :class="{ selected: selectedFaturas.includes(fatura.id) }">
						<td v-if="!fatura.link" class="checkbox">
							<label>
								<input type="checkbox" :checked="selectedFaturas.includes(fatura.id)"
									@change="toggleSelection(fatura)" />
								<span></span>
							</label>
						</td>
						<td v-else>

						</td>
						<td>
							<BadgeCircle v-if="fatura.status === 'PAGO' && fatura.baixa_manual" color="green2" />
							<BadgeCircle v-else-if="fatura.status === 'PAGO'" color="green" />
							<BadgeCircle v-else-if="fatura.status === 'PENDENTE'" color="red" />
							<BadgeCircle v-else-if="fatura.status === 'AGUARDANDO CONFIRMAÇÂO DE AULA'" color="yellow" />
							<BadgeCircle v-else />
						</td>
						<td>{{ fatura.codigo_processo }}</td>
						<td>{{ fatura.tipo }}</td>
						<td>{{ fatura.nome }}</td>
						<td>{{ utils.formatOnlyDate(fatura.created_at.split(" ")[0]) }}</td>
						<td>{{ utils.formatarValor(fatura.valor) }}</td>
						<td>
							<div class="actions">
								<div>
									<IconButton @handle:click="openInfoModal(fatura)" color="secondary" title="Detalhes"
										:disabled="loadingActions">
										<InformationOutline :size="19" />
									</IconButton>
								</div>
								<div v-if="fatura.link">
									<IconButton @handle:click="ticketPDFDownlaod(fatura.link)" color="success" title="Baixar boleto"
										:disabled="loadingActions">
										<FileOutline :size="19" />
									</IconButton>
								</div>
								<div v-if="fatura.link">
									<IconButton @handle:click="getBordero(fatura)" color="primary" title="Visualizar Borderô"
										:disabled="loadingActions">
										<EyeOutline :size="19" />
									</IconButton>
								</div>
								<div v-if="fatura.link && !fatura.pago">
									<IconButton @handle:click="actionModalCancel(fatura)" color="danger" title="Cancelar Boleto"
										:disabled="loadingActions">
										<Close :size="19" />
									</IconButton>
								</div>
							</div>
						</td>
					</tr>
				</Table>
			</div>
		</Box>

		<InfoModal ref="infoModal" />

		<ConfirmModal ref="confirmModal" :value="faturasSelectedValue" @handle:confirm="ticketGenerate"
			:loading="loadingConfirm" />
	</PageContainer>
</template>

<script>
import * as spinners from 'epic-spinners'
import urlApi from '@/router/urlApiV2'
import utils from '../../services/utils'
import _export from '../../services/export'
import PageContainer from '../core/PageContainer'
import Box from '../core/Box'
import RadioButtons from '../core/RadioButtons'
import Period from '../core/Period'
import Select from '../core/Select'
import Input from '../core/Input'
import Button from '../core/Button'
import Table from '../core/Table'
import Paginate from '../core/Paginate'
import StatusLegend from '../core/StatusLegend'
import BadgeCircle from '../core/BadgeCircle'
import IconButton from '../core/IconButton'
import InfoModal from './InfoModal'
import ConfirmModal from './ConfirmModal'
import CloseCircleOutline from 'icons/CloseCircleOutline'
import Magnify from 'icons/Magnify'
import InformationOutline from 'icons/InformationOutline'
import EyeOutline from 'icons/EyeOutline'
import Close from 'icons/Close'
import Barcode from 'icons/Barcode'
import FileOutline from 'icons/FileOutline'

export default {
	name: 'Index',
	components: {
		...spinners,
		CloseCircleOutline,
		Magnify,
		InformationOutline,
		EyeOutline,
		Close,
		Barcode,
		FileOutline,

		PageContainer,
		Box,
		RadioButtons,
		Period,
		Select,
		Input,
		Button,
		Table,
		Paginate,
		StatusLegend,
		BadgeCircle,
		IconButton,
		InfoModal,
		ConfirmModal,
	},
	data() {
		return {
			schema: localStorage.schema,
			utils: utils,
			consult: 'period',
			initialDate: '',
			finalDate: '',
			processo: '',
			status: '',
			aluno: '',
			cpf: '',
			barcode: '',
			faturasSelectedValue: 0,
			consultOptions: [
				{ value: "period", label: "Período" },
				{ value: "student", label: "Nome do Aluno" },
				{ value: "barcode", label: "Código de barras" },
			],
			processoOptions: [
				{ value: 1, text: 'Teórico' },
				{ value: 2, text: 'Prático' },
				{ value: 8, text: 'Prática (Moto)' },
				{ value: 3, text: 'Inclusão' },
				{ value: 9, text: 'Inclusão (Moto)' },
				{ value: 4, text: 'Reciclagem' },
				{ value: 5, text: 'Renovação' },
			],
			statusOptions: [
				{ value: 2, text: 'Pago' },
				{ value: 1, text: 'Pendente' },
				{ value: 4, text: 'Gratuidade' },
				{ value: 7, text: 'Aguardando confirmação da aula' },
			],
			alunos: [],
			faturas: [],
			selectedFaturas: [],
			statusLegendOptions: [
				{ legend: 'Pago', color: 'green' },
				{ legend: 'Pago (baixa manual)', color: 'green2' },
				{ legend: 'Pendente', color: 'red' },
				{ legend: 'Aguardando confirmação de aula', color: 'yellow' },
				{ legend: 'Gratuidade' },
			],
			loading: false,
			loadingAluno: false,
			loadingActions: false,
			loadingConfirm: false,
		}
	},
	async created() {
		await this.loadAlunos()
	},
	methods: {
		async handleSubmit() {
			if (this.validateInputs()) {
				this.fetchFaturas()
			}
		},
		validateInputs() {
			switch (this.consult) {
				case 'period':
					if (!this.processo) {
						this.toast('Preencha o processo desejado.')
						return false
					}

					if ((!this.initialDate && this.finalDate) || (this.initialDate && !this.finalDate)) {
						this.toast('Preencha o todas as datas.')
						return false
					}

					if (this.initialDate && this.finalDate) {
						const initial = new Date(this.initialDate)
						const final = new Date(this.finalDate)

						if (initial > final) {
							this.toast('A data inicial deve ser menor ou igual à data final.')
							return false
						}
					}

					break
				case 'student':
					if (!this.aluno && !this.cpf) {
						this.toast('Pelo menos um dos campos (Aluno ou CPF) deve estar preenchido.')
						return false
					}

					break
				case 'barcode':
					if (!this.barcode) {
						this.toast('Preencha o campo "Código de barras".')
						return false
					}

					break
			}

			return true
		},
		async loadAlunos() {
			this.loadingAluno = true
			this.alunosOptions = []

			await this.$http.get(`${urlApi}/alunos/consultar/select/${this.schema}`)
				.then(response => {
					this.alunos = response.body.dados
				})
				.catch(err => {
					this.toast(err)
				})
				.finally(() => {
					this.loadingAluno = false
				})
		},
		async fetchFaturas() {
			this.loading = true

			const params = {}

			switch (this.consult) {
				case 'period':
					params.initialDate = this.initialDate
					params.finalDate = this.finalDate
					params.processo = this.processo
					params.status = this.status

					break
				case 'student':
					params.aluno = this.aluno
					params.cpf = this.cpf

					break
				case 'barcode':
					params.barcode = this.barcode

					break
			}

			await this.$http.get(`${urlApi}/faturamento/faturas`, {
				params,
			})
				.then(response => {
					if (response.body.dados.faturas.length < 1) {
						this.toast('Sem resultados para a sua pesquisa.')
					}

					this.pago = response.body.dados.pagos
					this.pendente = response.body.dados.pendentes
					this.gratuidade = response.body.dados.gratuidade
					this.faturas = response.body.dados.faturas
				})
				.catch(err => {
					this.toast('Erro ao buscar faturas.')
				})
				.finally(() => {
					this.loading = false
				})
		},
		clearInputs() {
			this.initialDate = ''
			this.finalDate = ''
			this.processo = ''
			this.status = ''
			this.aluno = ''
			this.cpf = ''
			this.barcode = ''
		},
		async ticketGenerate() {
			this.loadingConfirm = true

			const data = {
				id: this.selectedFaturas,
			}

			await this.$http.post(`${urlApi}/boleto/criar`, data)
				.then(async response => {
					_export.downloadPDF(response.body.dataPDF, 'relacao de alunos')

					await this.fetchFaturas()

					this.selectedFaturas = []
					this.faturasSelectedValue = 0
				})
				.catch(err => {
					this.toast(err.body.mensagem)
				})
				.finally(() => {
					this.loadingConfirm = false
					this.$refs.confirmModal.close()
				})
		},
		selectAll(isChecked) {
			if (isChecked) {
				this.selectedFaturas = this.faturas
					.filter(fatura => !fatura.link)
					.map((fatura) => fatura.id)
			} else {
				this.selectedFaturas = []
			}
		},
		toggleSelection(fatura) {
			const index = this.selectedFaturas.indexOf(fatura.id)
			if (index === -1) {
				this.selectedFaturas.push(fatura.id)
			} else {
				this.selectedFaturas.splice(index, 1)
			}
		},
		async ticketPDFDownlaod(link) {
			this.loadingActions = true

			await this.$http.get(`${urlApi}/boleto/show/${this.schema}/${link}`, {
				responseType: 'blob',
			})
				.then(response => response.blob())
				.then(response => {
					_export.downloadPDFBlob(response, 'boleto')
				})
				.catch(err => {
					this.toast('Erro ao tentar baixar o boleto.')
				})
				.finally(() => {
					this.loadingActions = false
				})
		},
		openInfoModal(fatura) {
			this.$refs.infoModal.open(fatura)
		},
		openConfirmModal() {
			const selectedFaturas = this.faturas.filter(fatura => this.selectedFaturas.includes(fatura.id))

			const ticketValue = selectedFaturas.reduce((total, fatura) => total + parseFloat(fatura.valor), 0)

			this.faturasSelectedValue = ticketValue

			this.$refs.confirmModal.open()
		},
		actionModalCancel() {
			this.toast('Entre em contato com o financeiro!')
		},
		async getBordero(fatura) {
			this.loadingActions = true

			const data = { id: fatura.id, }

			await this.$http.post(`${urlApi}/boleto/bordero`, data)
				.then(response => {
					_export.downloadPDF(response.body.dataPDF, 'bordero')
				})
				.catch(err => {
					this.toast('Erro ao gerar borderô.')
				})
				.finally(() => {
					this.loadingActions = false
				})
		},
		toast(text) {
			this.$toasted.show(text, {
				icon: 'fa-times',
				position: 'top-right',
				duration: 5000,
				fullWidth: false
			})
		},
	},
}
</script>

<style lang="scss" scoped>
.faturas-container {
	display: flex;
	flex-direction: column;
	gap: 2.5rem;

	form {
		display: flex;
		flex-direction: column;
		gap: 2.5rem;

		.radio {
			display: flex;
			flex-direction: column;
			gap: 1rem;

			span {
				font-size: 1rem;
				font-weight: 600;
				line-height: 21.82px;
				color: #606264;
			}
		}

		.inputs {
			display: flex;
			flex-direction: column;
			gap: 1.5rem;

			.inputs-row {
				display: flex;
				flex-wrap: wrap;
				gap: 1.5rem;

				>div {
					flex: 1;
				}

				.input-date {
					min-width: 22.375rem;
				}
			}
		}

		.buttons {
			align-self: flex-end;

			display: flex;
			gap: 0.5rem;
		}
	}

	.table-container {
		display: flex;
		flex-direction: column;
		gap: 1rem;

		.infos {
			display: flex;
			flex-wrap: wrap;
			align-items: flex-end;
			gap: 1.5rem;
		}

		.table-header {
			display: flex;
			flex-wrap: wrap;
			justify-content: space-between;
			align-items: center;
			gap: 1rem;

			.table-legend {
				display: flex;
				flex-wrap: wrap;
				align-items: center;
				gap: 0.625rem;

				>div {
					display: flex;
					align-items: center;
					gap: 0.5rem;
				}
			}
		}

		.checkbox {
			label {
				margin: 0;
				display: flex;
				align-items: center;
				gap: 0.75rem;
				font-size: 0.875rem;
				font-weight: 600;
				line-height: 19.1px;
				color: #606264;
				cursor: pointer;

				input[type="checkbox"] {
					display: none;
				}

				span {
					position: relative;
					display: inline-block;
					width: 1rem;
					height: 1rem;
					border: 2px solid #DFDCDC;
					border-radius: 2px;
					background-color: #FAFAFA;
					transition: background-color 0.2s, border-color 0.2s;
				}

				input[type="checkbox"]:checked+span {
					background-color: #1C2D5A;
				}

				input[type="checkbox"]:checked+span::after {
					content: '✓';
					color: white;
					font-size: 8px;
					position: absolute;
					top: 55%;
					left: 50%;
					transform: translate(-50%, -50%);
				}
			}
		}

		.actions {
			display: flex;
			align-items: center;

			div {
				padding: 0 0.5rem;
				display: flex;
				justify-content: center;

				&:not(:first-child) {
					border-left: 1px solid #ECECEC;
				}

				&:not(:last-child) {
					border-right: 1px solid #ECECEC;
				}
			}
		}
	}
}
</style>
